<template>
  <div class="dynamic-quiz-paper">
    <div
      v-for="(question, index) in questions"
      :key="question.key || index"
      class="question-card"
    >
      <div class="question-title">
        {{ index + 1 }}.{{ question.label }}
        <span class="question-mode">（{{ question.mode === 'multi' ? '多选' : '单选' }}）</span>
      </div>
      <div v-if="question.description" class="question-description">
        {{ question.description }}
      </div>

      <div class="option-list">
        <div
          v-for="option in question.options || []"
          :key="option.value"
          :class="optionClasses(question, option)"
          @click="toggleOption(question, option.value)"
        >
          <span class="option-prefix">{{ optionPrefix(option.value) }}</span>
          <span class="option-label">{{ option.label }}</span>
        </div>
      </div>

      <div v-if="!readonly && question.mode === 'multi'" class="question-actions">
        <van-button
          size="small"
          type="primary"
          round
          class="check-answer-btn"
          @click="submitQuestion(question)"
        >
          提交答案
        </van-button>
      </div>

      <div v-if="showFeedback(question)" :class="feedbackClasses(question)">
        <div class="feedback-status">
          {{ isQuestionCorrect(question) ? '回答正确' : '回答错误' }}
          <span class="feedback-icon">{{ isQuestionCorrect(question) ? '✓' : '×' }}</span>
        </div>
        <div class="feedback-text">
          你的答案：{{ formatSelectedAnswerLetters(question) }}
        </div>
        <div class="feedback-text">
          正确答案：{{ formatCorrectAnswerLetters(question) }}
        </div>
        <div v-if="questionExplanation(question)" class="feedback-text">
          解析：{{ questionExplanation(question) }}
        </div>
      </div>
    </div>

    <van-empty v-if="!questions.length" description="暂无题目" />
  </div>
</template>

<script>
import Vue from 'vue'
import { Button, Empty, Toast } from 'vant'

Vue.use(Button).use(Empty).use(Toast)

export default {
  name: 'DynamicQuizPaper',
  props: {
    paperConfig: {
      type: Object,
      default: () => ({})
    },
    answerConfig: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Object,
      default: () => ({})
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localAnswers: {},
      feedbackMap: {},
      debugEmitCount: 0
    }
  },
  computed: {
    questions() {
      return Array.isArray(this.paperConfig && this.paperConfig.questions) ? this.paperConfig.questions : []
    },
    answerQuestionMap() {
      const list = Array.isArray(this.answerConfig && this.answerConfig.questions) ? this.answerConfig.questions : []
      return list.reduce((acc, item) => {
        if (item && item.key) {
          acc[item.key] = item
        }
        return acc
      }, {})
    }
  },
  watch: {
    value: {
      handler(val) {
        this.debugLog('watch:value:start', val)
        var next = this.normalizeAnswers(val)
        if (JSON.stringify(next) === JSON.stringify(this.localAnswers)) {
          this.debugLog('watch:value:skip (no change)')
          return
        }
        this.localAnswers = next
        this.feedbackMap = this.readonly ? this.buildReadonlyFeedbackMap() : {}
        this.debugLog('watch:value:after-normalize', {
          questionCount: this.questions.length,
          answerKeys: Object.keys(this.localAnswers),
          feedbackKeys: Object.keys(this.feedbackMap)
        })
        this.emitStateChange()
      },
      immediate: true,
      deep: true
    },
    readonly(val) {
      this.debugLog('watch:readonly', val)
      this.feedbackMap = val ? this.buildReadonlyFeedbackMap() : {}
      this.emitStateChange()
    }
  },
  methods: {
    debugLog(step, payload) {
      console.log('[dynamic-quiz-paper]', step, payload)
    },
    normalizeAnswers(value) {
      const answers = value && typeof value === 'object' ? value : {}
      const normalized = Object.keys(answers).reduce((acc, key) => {
        const current = answers[key]
        const selectedOptionValues = Array.isArray(current && current.selectedOptionValues)
          ? current.selectedOptionValues.map(String)
          : []
        acc[key] = { selectedOptionValues }
        return acc
      }, {})
      this.debugLog('normalizeAnswers', {
        rawKeys: Object.keys(answers),
        normalizedKeys: Object.keys(normalized)
      })
      return normalized
    },
    buildReadonlyFeedbackMap() {
      const feedbackMap = this.questions.reduce((acc, question) => {
        acc[question.key] = {
          submitted: true,
          isCorrect: this.computeIsCorrect(question)
        }
        return acc
      }, {})
      this.debugLog('buildReadonlyFeedbackMap', {
        questionCount: this.questions.length,
        feedbackKeys: Object.keys(feedbackMap)
      })
      return feedbackMap
    },
    getSelectedValues(question) {
      var entry = this.localAnswers[question.key]
      return (entry && entry.selectedOptionValues) || []
    },
    getCorrectValues(question) {
      const answerQuestion = this.answerQuestionMap[question.key]
      return Array.isArray(answerQuestion && answerQuestion.correctOptionValues)
        ? answerQuestion.correctOptionValues.map(String)
        : []
    },
    toggleOption(question, optionValue) {
      if (this.readonly) return
      const selected = [...this.getSelectedValues(question)]
      const value = String(optionValue)
      if (question.mode === 'multi') {
        const index = selected.indexOf(value)
        if (index >= 0) {
          selected.splice(index, 1)
        } else {
          selected.push(value)
        }
        this.$set(this.localAnswers, question.key, { selectedOptionValues: selected })
        this.emitStateChange()
        return
      }
      if (selected[0] === value) {
        selected.length = 0
      } else {
        selected.splice(0, selected.length, value)
      }
      this.$set(this.localAnswers, question.key, { selectedOptionValues: selected })
      if (selected.length === 0) {
        this.$delete(this.feedbackMap, question.key)
        this.emitStateChange()
      } else {
        this.submitQuestion(question)
      }
    },
    submitQuestion(question) {
      const selected = this.getSelectedValues(question)
      if (!selected.length) {
        Toast('请先选择答案')
        return
      }
      this.$set(this.feedbackMap, question.key, {
        submitted: true,
        isCorrect: this.computeIsCorrect(question)
      })
      this.emitStateChange()
    },
    computeIsCorrect(question) {
      const selected = [...this.getSelectedValues(question)].sort()
      const correct = [...this.getCorrectValues(question)].sort()
      if (question.mode === 'single' && selected.length !== 1) {
        return false
      }
      if (selected.length !== correct.length) {
        return false
      }
      return selected.every((item, index) => item === correct[index])
    },
    showFeedback(question) {
      var fb = this.feedbackMap[question.key]
      return !!(fb && fb.submitted)
    },
    isQuestionCorrect(question) {
      var fb = this.feedbackMap[question.key]
      return fb && fb.isCorrect === true
    },
    optionClasses(question, option) {
      const selected = this.getSelectedValues(question)
      const correct = this.getCorrectValues(question)
      const isSelected = selected.includes(String(option.value))
      const isCorrectOption = correct.includes(String(option.value))
      const hasFeedback = this.showFeedback(question)

      return {
        'option-item': true,
        'option-item--readonly': this.readonly,
        'option-item--selected': isSelected && !hasFeedback,
        'option-item--correct-selected': hasFeedback && isCorrectOption && isSelected,
        'option-item--correct-reveal': hasFeedback && isCorrectOption && !isSelected,
        'option-item--wrong': hasFeedback && isSelected && !isCorrectOption
      }
    },
    feedbackClasses(question) {
      return {
        'feedback-panel': true,
        'feedback-panel--correct': this.isQuestionCorrect(question),
        'feedback-panel--wrong': !this.isQuestionCorrect(question)
      }
    },
    optionPrefix(value) {
      return `${String(value || '').toUpperCase()}.`
    },
    /** 反馈区仅展示选项名（A/B/C…），不重复展示选项长文案，节省纵向空间 */
    formatValuesAsLetters(values) {
      if (!values || !values.length) return ''
      return values.map(function (v) {
        return String(v).toUpperCase()
      }).join(' / ')
    },
    formatSelectedAnswerLetters(question) {
      const selected = this.getSelectedValues(question)
      if (!selected.length) return '未作答'
      return this.formatValuesAsLetters(selected)
    },
    formatCorrectAnswerLetters(question) {
      const correct = this.getCorrectValues(question)
      if (!correct.length) return '-'
      return this.formatValuesAsLetters(correct)
    },
    questionExplanation(question) {
      const aq = this.answerQuestionMap[question.key]
      if (!aq || typeof aq !== 'object') return ''
      const text = aq.explanation || aq.analysis || ''
      return typeof text === 'string' ? text.trim() : ''
    },
    getAnswerContent() {
      return Object.keys(this.localAnswers).reduce((acc, key) => {
        var entry = this.localAnswers[key]
        const selectedOptionValues = (entry && entry.selectedOptionValues) || []
        acc[key] = { selectedOptionValues: [...selectedOptionValues] }
        return acc
      }, {})
    },
    areAllQuestionsCorrect() {
      if (!this.questions.length) return false
      return this.questions.every(function (question) {
        var fb = this.feedbackMap[question.key]
        return fb && fb.submitted && fb.isCorrect
      }.bind(this))
    },
    emitStateChange() {
      this.debugEmitCount += 1
      this.debugLog('emitStateChange', {
        emitCount: this.debugEmitCount,
        questionCount: this.questions.length,
        answerKeys: Object.keys(this.localAnswers),
        feedbackKeys: Object.keys(this.feedbackMap),
        allQuestionsCorrect: this.areAllQuestionsCorrect()
      })
      this.$emit('state-change', {
        answerContent: this.getAnswerContent(),
        allQuestionsCorrect: this.areAllQuestionsCorrect()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.dynamic-quiz-paper {
  .question-card {
    margin-bottom: 16px;
    padding: 16px;
    background: #fff;
    border-radius: 8px;
  }

  .question-title {
    font-size: 16px;
    line-height: 1.5;
    color: #323233;
    font-weight: 600;
  }

  .question-mode {
    font-weight: 400;
  }

  .question-description {
    margin-top: 8px;
    font-size: 13px;
    line-height: 1.5;
    color: #969799;
  }

  .option-list {
    margin-top: 16px;
  }

  .option-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 12px;
    padding: 12px;
    border: 1px solid #ebedf0;
    border-radius: 6px;
    background: #fff;
    transition: all 0.2s;

    &--selected {
      border-color: var(--qn-theme);
      background: #eef5ff;
      color: var(--qn-theme);
    }

    &--correct-selected {
      border-color: #73c26b;
      background: #edf9eb;
      color: #2d6f2d;
      box-shadow: 0 0 0 1px rgba(115, 194, 107, 0.35);
    }

    &--correct-reveal {
      border: 1.5px dashed #73c26b;
      background: #fff;
      color: #2d6f2d;
    }

    &--wrong {
      border-color: #d96565;
      background: #fff1f1;
      color: #9e2f2f;
    }

    &--readonly {
      cursor: default;
    }
  }

  .option-prefix {
    width: 24px;
    flex-shrink: 0;
    font-weight: 600;
  }

  .option-label {
    flex: 1;
    line-height: 1.5;
    word-break: break-word;
  }

  .question-actions {
    margin-top: 12px;
    text-align: right;
  }

  .check-answer-btn {
    min-width: 108px;
    background: var(--qn-theme);
    border: none;
  }

  .feedback-panel {
    margin-top: 12px;
    padding: 12px;
    border-radius: 6px;
    font-size: 14px;
    line-height: 1.6;

    &--correct {
      background: #edf9eb;
      color: #2d6f2d;
    }

    &--wrong {
      background: #fff1f1;
      color: #9e2f2f;
    }
  }

  .feedback-status {
    font-weight: 600;
  }

  .feedback-icon {
    margin-left: 6px;
  }

  .feedback-text {
    margin-top: 4px;
  }
}
</style>
